// Retries (V6 plan 13.3.1 "The wire", row H16). Every write procedure and the upload route
// honour an `Idempotency-Key` header: the first call with a key runs and its answer is kept for
// 24 hours; a retry with the same key and the same request answers that answer again, with
// `Idempotency-Replayed: true`, and does nothing. `runs.command` takes its `command.commandId`
// as the key when the header is absent. No header means no idempotency.
//
// Not covered: a procedure that mints or clears the cookie (`sessionChanging`; a replay could
// not set it), and one that declares `replays: false` because its answer holds a secret that is
// shown once and never stored (apiKeys.create).
// The request header and its longest value (IDEMPOTENCY_KEY_HEADER, IDEMPOTENCY_KEY_MAX_LENGTH:
// a key is any string of 1 to 255 characters) are in access.ts, beside the meta that declares it.
/** The response header on a replayed answer (its value is `true`). */
export const IDEMPOTENCY_REPLAYED_HEADER = "Idempotency-Replayed";
/** How long a key's answer is kept. */
export const IDEMPOTENCY_KEY_TTL_HOURS = 24;
/** 400: the header is empty or longer than IDEMPOTENCY_KEY_MAX_LENGTH. */
export const INVALID_IDEMPOTENCY_KEY = "invalid_idempotency_key";
/**
 * 409: the key was used for another request (another body) by this caller on this procedure.
 * Its data is IdempotencyKeyReusedData.
 */
export const IDEMPOTENCY_KEY_REUSED = "idempotency_key_reused";
/** 409: the first call with this key has not answered yet; retry after it does. */
export const IDEMPOTENCY_IN_FLIGHT = "idempotency_in_flight";
/** 400 (billing.selectOffer, meta `idempotencyKey`): the header is required there. */
export const IDEMPOTENCY_KEY_REQUIRED = "idempotency_key_required";
/** 400 (billing.selectOffer, meta `idempotencyKey`): the header must equal input.requestId. */
export const IDEMPOTENCY_KEY_MISMATCH = "idempotency_key_mismatch";
/** The procedure whose input names its own key: `command.commandId`, used when no header is sent. */
export const COMMAND_ID_KEYED_PROCEDURE = "runs.command";
/** The procedures that start a run: a reused key there names the run its first call started. */
export const RUN_STARTING_PROCEDURES = ["runs.start", "runs.replay"];
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
function sortedKeys(value) {
    if (Array.isArray(value))
        return value.map(sortedKeys);
    if (!isRecord(value))
        return value;
    const toJSON = value.toJSON;
    if (typeof toJSON === "function")
        return sortedKeys(toJSON.call(value));
    // A null-prototype object, so every key is an own property: assigned into `{}`, a key named
    // `__proto__` would hit Object.prototype's setter and drop out of the canonical form, and two
    // requests that differ only there would hash the same.
    const sorted = Object.create(null);
    for (const key of Object.keys(value).sort())
        sorted[key] = sortedKeys(value[key]);
    return sorted;
}
/**
 * A request as canonical JSON: every object's keys sorted at every depth (an object with a
 * `toJSON`, a Date, as what it serializes to), the order of arrays kept. Every own key is kept,
 * `__proto__` included. The server holds a key to the sha-256 of it; a client keys its retries
 * by it, one key per distinct request (D73).
 */
export function canonicalJson(value) {
    return JSON.stringify(sortedKeys(value)) ?? "null";
}
/**
 * Whether a procedure honours an Idempotency-Key, from its meta: every write, except one that
 * mints or clears the cookie and one that declares `replays: false`.
 */
export function honoursIdempotencyKey(meta) {
    if (typeof meta !== "object" || meta === null)
        return false;
    const { access, sessionChanging, replays } = meta;
    return access === "write" && sessionChanging !== true && replays !== false;
}
//# sourceMappingURL=idempotency.js.map